import { useEffect, useState, useCallback } from "react";
import { useT } from "../i18n/I18nProvider";
import { fetchSources, adminTouchSource, adminNbpStatus, adminAuthCheck, adminGusStatus, adminRefreshGus } from "../lib/api";
import DataErrorNotice from "../components/DataErrorNotice";
import { Lock, CheckCircle2, LogOut, RefreshCw, CalendarClock, AlertCircle, Database, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";

const STORAGE_KEY = "hejpoland_admin_secret";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("fr-FR", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

export default function AdminSources() {
  const { t } = useT();
  const [secret, setSecret] = useState(() => sessionStorage.getItem(STORAGE_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [authError, setAuthError] = useState("");
  const [pwInput, setPwInput] = useState("");
  const [sources, setSources] = useState([]);
  const [nbpStatus, setNbpStatus] = useState(null);
  const [gusStatus, setGusStatus] = useState(null);
  const [gusRefreshing, setGusRefreshing] = useState(false);
  const [loadErr, setLoadErr] = useState(false);

  // Validate stored secret on mount
  useEffect(() => {
    if (!secret) return;
    adminAuthCheck(secret)
      .then(() => setAuthed(true))
      .catch(() => { setSecret(""); sessionStorage.removeItem(STORAGE_KEY); });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const reload = useCallback(async () => {
    if (!authed) return;
    setLoadErr(false);
    try {
      const [srcs, nbp, gus] = await Promise.all([
        fetchSources(),
        adminNbpStatus(secret),
        adminGusStatus(secret).catch(() => null),
      ]);
      setSources(srcs);
      setNbpStatus(nbp);
      setGusStatus(gus);
    } catch (e) {
      console.error("[AdminSources] load failed:", e?.message);
      setLoadErr(true);
    }
  }, [authed, secret]);

  useEffect(() => { reload(); }, [reload]);

  const login = async (e) => {
    e.preventDefault();
    setAuthError("");
    try {
      await adminAuthCheck(pwInput);
      sessionStorage.setItem(STORAGE_KEY, pwInput);
      setSecret(pwInput); setAuthed(true); setPwInput("");
    } catch {
      setAuthError(t("adminSources.invalidSecret"));
    }
  };

  const logout = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setSecret(""); setAuthed(false);
  };

  const touch = async (id) => {
    try {
      const res = await adminTouchSource(id, secret);
      toast.success(`${t("adminSources.touched")} (${id}) — ${formatDate(res.last_updated)}`);
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const refreshGus = async () => {
    setGusRefreshing(true);
    try {
      const res = await adminRefreshGus(secret);
      const ok = res?.cities_ok || 0;
      const total = res?.cities_total || 0;
      toast.success(`${t("adminSources.gusRefreshSuccess")} — ${ok}/${total} villes`);
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("adminSources.gusRefreshError"));
    } finally {
      setGusRefreshing(false);
    }
  };

  if (!authed) {
    return (
      <div className="max-w-md mx-auto px-4 py-24" data-testid="admin-login">
        <div className="bg-white border border-slate-200 rounded-2xl p-8">
          <div className="w-12 h-12 rounded-2xl bg-[#0056D2]/10 flex items-center justify-center mb-5">
            <Lock className="w-5 h-5 text-[#0056D2]" />
          </div>
          <h1 className="font-display text-2xl font-semibold text-slate-900">{t("adminSources.title")}</h1>
          <p className="text-sm text-slate-600 mt-2 mb-6">{t("adminSources.needSecret")}.</p>
          <form onSubmit={login} className="space-y-3">
            <input
              type="password"
              autoFocus
              value={pwInput}
              onChange={(e) => setPwInput(e.target.value)}
              placeholder={t("adminSources.promptSecret")}
              className="w-full px-4 py-2.5 border border-slate-200 rounded-xl bg-slate-50 focus:bg-white focus:ring-2 focus:ring-[#0056D2] focus:border-transparent outline-none"
              data-testid="admin-secret-input"
            />
            {authError && (
              <div className="flex items-center gap-2 text-sm text-[#DC143C]">
                <AlertCircle className="w-4 h-4" />{authError}
              </div>
            )}
            <button type="submit" className="w-full bg-[#0056D2] hover:bg-[#0047AD] text-white font-medium py-2.5 rounded-full transition" data-testid="admin-login-submit">
              Se connecter
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-12 md:py-16" data-testid="admin-sources-page">
      <header className="flex items-center justify-between flex-wrap gap-4 mb-8">
        <div>
          <span className="hej-chip"><span className="w-1.5 h-1.5 rounded-full bg-[#DC143C]"></span>Admin</span>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mt-3">{t("adminSources.title")}</h1>
          <p className="text-slate-600 mt-2 text-sm max-w-2xl">{t("adminSources.intro")}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <a href="../admin/numbeo" className="px-4 py-2 rounded-full bg-[#0056D2] hover:bg-[#0047AD] text-white text-sm font-medium inline-flex items-center gap-1.5" data-testid="goto-admin-numbeo"><Database className="w-3.5 h-3.5" /> Données Numbeo</a>
          <a href="../admin/data" className="px-4 py-2 rounded-full bg-white border border-slate-200 hover:bg-slate-50 text-sm font-medium" data-testid="goto-admin-data">{t("adminSources.goToData")}</a>
          <button onClick={reload} className="px-4 py-2 rounded-full bg-white border border-slate-200 hover:bg-slate-50 text-sm flex items-center gap-1.5" data-testid="admin-reload"><RefreshCw className="w-3.5 h-3.5" /> Recharger</button>
          <button onClick={logout} className="px-4 py-2 rounded-full bg-slate-100 hover:bg-slate-200 text-sm flex items-center gap-1.5" data-testid="admin-logout"><LogOut className="w-3.5 h-3.5" /> {t("adminSources.logout")}</button>
        </div>
      </header>

      {loadErr && <div className="mb-6"><DataErrorNotice onRetry={reload} /></div>}

      {nbpStatus && (
        <section className="bg-white border border-slate-200 rounded-2xl p-6 mb-8" data-testid="nbp-status-card">
          <h2 className="font-display font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <CalendarClock className="w-4 h-4 text-[#0056D2]" /> {t("adminSources.nbpStatus")}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
            <div><p className="text-xs text-slate-500 uppercase tracking-wide">{t("adminSources.nbpCachedAt")}</p><p className="font-medium text-slate-900 mt-1">{formatDate(nbpStatus.cached_at)}</p></div>
            <div><p className="text-xs text-slate-500 uppercase tracking-wide">{t("adminSources.nbpEffectiveDate")}</p><p className="font-medium text-slate-900 mt-1">{nbpStatus.effective_date || "—"}</p></div>
            <div><p className="text-xs text-slate-500 uppercase tracking-wide">{t("adminSources.nbpCurrencies")}</p><p className="font-medium text-slate-900 mt-1">{nbpStatus.currencies?.join(", ") || "—"}</p></div>
          </div>
          {nbpStatus.is_fallback && (
            <div className="mt-4 flex items-center gap-2 text-sm text-amber-700"><AlertCircle className="w-4 h-4" />Mode fallback actif (API NBP indisponible).</div>
          )}
        </section>
      )}

      <section className="bg-white border border-slate-200 rounded-2xl p-6 mb-8" data-testid="gus-status-card">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
          <div>
            <h2 className="font-display font-semibold text-slate-900 flex items-center gap-2">
              <Database className="w-4 h-4 text-[#DC143C]" /> {t("adminSources.gusTitle")}
            </h2>
            <p className="text-xs text-slate-500 mt-1.5 max-w-2xl leading-relaxed">{t("adminSources.gusIntro")}</p>
          </div>
          <button
            onClick={refreshGus}
            disabled={gusRefreshing}
            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition whitespace-nowrap ${
              gusRefreshing ? "bg-slate-100 text-slate-400 cursor-not-allowed" : "bg-[#DC143C] hover:bg-[#B01030] text-white"
            }`}
            data-testid="gus-refresh-btn"
          >
            {gusRefreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            {gusRefreshing ? t("adminSources.gusRefreshing") : t("adminSources.gusRefresh")}
          </button>
        </div>

        <div className="text-xs text-slate-500 mb-3">
          <span className="uppercase tracking-wide">{t("adminSources.gusLastRefresh")}: </span>
          <span className="text-slate-900 font-medium tabular-nums" data-testid="gus-last-refresh">
            {gusStatus?.fetched_at ? formatDate(gusStatus.fetched_at) : t("adminSources.gusNoRefresh")}
          </span>
        </div>

        {gusStatus?.summary?.results && (
          <div className="overflow-x-auto rounded-xl border border-slate-200 mt-2" data-testid="gus-results-table">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold">Ville</th>
                  <th className="px-3 py-2 text-right font-semibold">{t("adminSources.gusPopulation")}</th>
                  <th className="px-3 py-2 text-center font-semibold">{t("adminSources.gusYear")}</th>
                  <th className="px-3 py-2 text-right font-semibold">{t("adminSources.gusPrice")}</th>
                  <th className="px-3 py-2 text-center font-semibold">{t("adminSources.gusYear")} / {t("adminSources.gusQuarter")}</th>
                  <th className="px-3 py-2 text-center font-semibold">Statut</th>
                </tr>
              </thead>
              <tbody>
                {gusStatus.summary.results.map((r) => (
                  <tr key={r.slug} className="border-t border-slate-100" data-testid={`gus-row-${r.slug}`}>
                    <td className="px-3 py-2 font-medium text-slate-900 capitalize">{r.slug}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.population?.toLocaleString("fr-FR") || "—"}</td>
                    <td className="px-3 py-2 text-center text-slate-500">{r.population_year || "—"}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.price_per_m2 ? `${r.price_per_m2.toLocaleString("fr-FR")} zł` : "—"}</td>
                    <td className="px-3 py-2 text-center text-slate-500">{r.price_year ? `${r.price_year} ${r.price_quarter || ""}` : "—"}</td>
                    <td className="px-3 py-2 text-center">
                      {r.ok ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="w-3 h-3" />{t("adminSources.gusOk")}</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[#DC143C]"><AlertCircle className="w-3 h-3" />{t("adminSources.gusFailed")}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-3 text-xs">
          <a href="https://bdl.stat.gov.pl/bdl/start" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#0056D2] hover:underline">
            <ExternalLink className="w-3 h-3" />
            {t("adminSources.gusOpenPortal")}
          </a>
        </div>
      </section>

      <div className="overflow-x-auto rounded-2xl border border-slate-200" data-testid="sources-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="px-4 py-3 text-left font-semibold">{t("adminSources.columnSource")}</th>
              <th className="px-4 py-3 text-left font-semibold">{t("adminSources.columnOfficial")}</th>
              <th className="px-4 py-3 text-left font-semibold">{t("adminSources.columnLastUpdated")}</th>
              <th className="px-4 py-3 text-left font-semibold">{t("adminSources.columnFrequency")}</th>
              <th className="px-4 py-3 text-right font-semibold">{t("adminSources.columnAction")}</th>
            </tr>
          </thead>
          <tbody>
            {sources.map((s) => (
              <tr key={s.id} className="border-t border-slate-100 hover:bg-slate-50/50" data-testid={`source-row-${s.id}`}>
                <td className="px-4 py-3 font-medium text-slate-900">{s.label_fr}</td>
                <td className="px-4 py-3 text-slate-600">
                  <div className="flex flex-wrap gap-1.5">
                    {(s.urls || []).map((u, i) => (
                      <a key={i} href={u.url} target="_blank" rel="noopener noreferrer" className="inline-block px-2 py-0.5 text-xs rounded-full bg-slate-100 hover:bg-[#0056D2]/10 text-slate-700 hover:text-[#0056D2] transition">
                        {u.name}
                      </a>
                    ))}
                  </div>
                </td>
                <td className="px-4 py-3 text-slate-600 tabular-nums whitespace-nowrap">{formatDate(s.last_updated)}</td>
                <td className="px-4 py-3 text-slate-500 text-xs">{s.frequency}</td>
                <td className="px-4 py-3 text-right">
                  {s.id === "gus_bdl" || s.id === "currency" ? (
                    <span className="inline-flex items-center gap-1 text-xs text-slate-400 italic px-3 py-1.5 whitespace-nowrap">auto</span>
                  ) : (
                    <button onClick={() => touch(s.id)} className="inline-flex items-center gap-1.5 text-xs font-medium bg-[#0056D2] hover:bg-[#0047AD] text-white px-3 py-1.5 rounded-full transition whitespace-nowrap" data-testid={`touch-${s.id}`}>
                      <CheckCircle2 className="w-3.5 h-3.5" /> {t("adminSources.touch")}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
